import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, VStack, HStack, Text, Input, FormControl, FormLabel, useToast,
    Box, Heading, Checkbox, Textarea, Flex, Badge, List, ListItem, ListIcon, Spacer, IconButton, Divider, Icon,
    Link, Image,
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
    useBreakpointValue, Tooltip
} from '@chakra-ui/react';
import {
    WarningIcon, RepeatIcon, AddIcon, MinusIcon, InfoIcon, WarningTwoIcon,
    ExternalLinkIcon, QuestionOutlineIcon, CheckCircleIcon
} from '@chakra-ui/icons';
import { AJUDA_ENTRADA_FOTO, AJUDA_ENTRADA_ESTOQUE, AJUDA_SO_CORRIGIR } from '../despacho/SecaoImagemDivergente';
import { FaPlay, FaExclamationTriangle, FaWarehouse, FaImage } from 'react-icons/fa';
import ProdutoImagem from "../ProdutoImagem";
import { reverterConferenciaProduto, marcarFuroEstoqueTaga } from '../../services/api';
import Notas from "../Notas";

export const ConfirmationModalDespacho = ({ isOpen, onClose, productToVerify, onConfirm, onRevert, handleAddObs, handleChangeTextObs, textObs, handleApagarObs, etapaAtual, onProcessIndividual, incompleteVendaIds, onMarkAsDelivered, onDarEntradaEstoque, onCorrigirImagem }) => {
    const isReadOnly = etapaAtual === 'processamento';
    const [inputQuantity, setInputQuantity] = useState('0');
    const [dataIssues, setDataIssues] = useState({ photo: false, name: false, code: false, brand: false, avaria: false });
    const [outroFornecedor, setOutroFornecedor] = useState({ checked: false, nome: '' });
    const [observation, setObservation] = useState('');
    const toast = useToast();
    const [isReverting, setIsReverting] = useState(false);

    const [furoEstoqueDialogOpen, setFuroEstoqueDialogOpen] = useState(false);
    const [furoEstoqueLoading, setFuroEstoqueLoading] = useState(false);
    const [furoEstoqueDone, setFuroEstoqueDone] = useState(false);
    const cancelFuroRef = useRef();
    const alertDialogSize = useBreakpointValue({ base: 'full', md: 'md' });

    useEffect(() => {
        setFuroEstoqueDone(false);
        setFuroEstoqueDialogOpen(false);
    }, [productToVerify?.itemVendaId]);

    const handleConfirmFuroEstoque = async () => {
        if (!productToVerify?.itemVendaId) {
            toast({ title: 'Item de venda não identificado.', status: 'error', isClosable: true });
            return;
        }
        setFuroEstoqueLoading(true);
        const res = await marcarFuroEstoqueTaga(productToVerify.itemVendaId);
        setFuroEstoqueLoading(false);
        if (res?.error) {
            toast({
                title: 'Erro ao marcar furo de estoque',
                description: res.error,
                status: 'error',
                duration: 5000,
                isClosable: true
            });
            return;
        }
        productToVerify.estoque_taga = 0;
        productToVerify.localizacao_estoque = null;
        setFuroEstoqueDone(true);
        setFuroEstoqueDialogOpen(false);
        toast({
            title: 'Furo de estoque TAGA registrado',
            description: `Qtd: ${res?.qtd_original ?? '-'} | Local: ${res?.loc_original ?? '-'}. A baixa não será revertida em cancelamento.`,
            status: 'success',
            duration: 6000,
            isClosable: true
        });
    };

    const canMarkAsDelivered = useMemo(() => {
        if (!productToVerify) return false;
        if (productToVerify.verificationDetails?.status !== 3) return false;

        const origem = productToVerify.origem?.toUpperCase();
        if (origem === 'ECOMMERCE' || origem === 'TAGA') return true;
        if (origem === 'MERCADO LIVRE') {
            const mode = productToVerify.envio?.modo_envio?.toLowerCase();
            return mode === 'not_specified' || mode === 'custom' || !mode;
        }
        return false;
    }, [productToVerify]);

    useEffect(() => {
        if (productToVerify) {
            const details = productToVerify.verificationDetails;
            const initialQty = details?.checkedQuantity;
            setInputQuantity(initialQty != null && initialQty !== '' ? String(initialQty) : '0');
            setDataIssues(details?.issues || { photo: false, name: false, code: false, brand: false, avaria: false });
            setOutroFornecedor(details?.outroFornecedor || { checked: false, nome: '' });
            setObservation(details?.observation || '');
        } else {
            setInputQuantity('0');
        }
    }, [productToVerify]);

    const handleQuantityChange = (e) => {
        const value = e.target.value;
        const sanitizedValue = value.replace(/[^0-9]/g, '');

        if (sanitizedValue === '') {
            setInputQuantity('0');
        } else {
            setInputQuantity(String(parseInt(sanitizedValue, 10)));
        }
    };

    const handleIncrement = () => {
        const currentValue = parseInt(inputQuantity, 10) || 0;
        setInputQuantity(String(currentValue + 1));
    };

    const handleDecrement = () => {
        const currentValue = parseInt(inputQuantity, 10) || 0;
        if (currentValue > 0) {
            setInputQuantity(String(currentValue - 1));
        }
    };

    const imagemPendenteCorrecao = dataIssues.photo && !productToVerify?.verificationDetails?.fotoCorrigidaAt;

    const reportedProblems = useMemo(() => {
        const problems = [];
        if (!productToVerify?.verificationDetails) return problems;
        const { checkedQuantity, issues } = productToVerify.verificationDetails;
        const expectedQuantity = productToVerify.quantity;
        const enteredQuantity = parseInt(checkedQuantity, 10);
        if (!isNaN(enteredQuantity) && enteredQuantity !== expectedQuantity) {
            // O anúncio vende a embalagem fechada: quantity conta EMBALAGENS, não peças.
            const emb = parseInt(productToVerify?.produtoEstoque?.quantidade_embalagem, 10);
            const sufixo = emb > 1
                ? ` — esperado ${expectedQuantity} × ${emb} = ${expectedQuantity * emb} peças`
                : '';
            problems.push(`Quantidade não confere (Esperado: ${expectedQuantity}, Conferido: ${enteredQuantity})${sufixo}`);
        }
        if (issues?.photo) problems.push("Problema com a Foto");
        if (issues?.name) problems.push("Problema com o Nome");
        if (issues?.code) problems.push("Problema com o Código");
        if (issues?.brand) problems.push("Problema com a Marca");
        if (issues?.avaria) problems.push("Produto com Avaria");
        return problems;
    }, [productToVerify]);

    const handleConfirmClick = () => {
        if (!productToVerify) return;
        const enteredQuantity = parseInt(inputQuantity, 10) || 0;
        const hasQuantityMismatch = enteredQuantity !== productToVerify.quantity;
        const hasReportedDataIssue = Object.values(dataIssues).some(v => v === true);
        const status = (hasQuantityMismatch || hasReportedDataIssue) ? 'problem' : 'success';
        const verificationDetails = {
            checkedQuantity: enteredQuantity,
            issues: dataIssues,
            outroFornecedor: outroFornecedor,
            observation: observation,
            verifiedAt: new Date().toISOString(),
        };
        const verifiedProduct = { ...productToVerify, verificationDetails };
        toast({
            title: status === 'problem' ? `Produto movido para 'Problemas'` : 'Produto verificado com Sucesso!',
            status: status === 'problem' ? 'warning' : 'success',
            duration: 3000,
            isClosable: true,
        });
        onConfirm(verifiedProduct, status);
        onClose();
    };

    const handleRevertClick = async () => {
        if (!productToVerify) return;
        setIsReverting(true);
        const success = await reverterConferenciaProduto(productToVerify.vendaId, productToVerify.produtoEstoqueId);
        if (success) {
            toast({
                title: 'Conferência Revertida!',
                description: 'O produto voltou para a lista de pendentes.',
                status: 'info',
                duration: 3000,
                isClosable: true,
            });
            onRevert(productToVerify);
            onClose();
        } else {
            toast({
                title: 'Erro ao reverter',
                description: 'Não foi possível reverter a conferência. Tente novamente.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
        setIsReverting(false);
    };

    const firstAnuncio = productToVerify?.produtoEstoque?.anuncios?.[0];
    const mlLink = firstAnuncio?.id_ml ? `https://produto.mercadolivre.com.br/MLB-${firstAnuncio.id_ml.substring(3)}` : null;
    const qtdEmbalagem = productToVerify?.produtoEstoque?.quantidade_embalagem;
    // Item vendido em embalagem fechada: productToVerify.quantity conta EMBALAGENS,
    // não peças soltas. Deixa a conta explícita para quem está conferindo.
    const vendeEmbalagemFechada = parseInt(qtdEmbalagem, 10) > 1;
    const totalPecasEsperadas = vendeEmbalagemFechada
        ? productToVerify?.quantity * parseInt(qtdEmbalagem, 10)
        : null;

    const envio = productToVerify?.envio || {};
    const isFlex = envio.flex_ml === 1;
    const modoEnvio = envio.modo_envio;

    if (!productToVerify) return null;
    const observationText = productToVerify?.verificationDetails?.observation;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '4xl' }} isCentered scrollBehavior="inside" returnFocusOnClose={false}>
            <ModalOverlay />
            <ModalContent mx={{ base: 0, md: 4 }}>
                <ModalHeader fontSize={{ base: 'md', md: 'lg' }} pr={10} wordBreak="break-word">Conferência: {productToVerify.orderId} / Produto: {productToVerify.name}</ModalHeader>
                <ModalCloseButton />
                <ModalBody py={4}>
                    {isReadOnly && (
                        <Box bg="blue.50" borderColor="blue.200" borderWidth="1px" p={3} borderRadius="md" mb={4}>
                            <HStack>
                                <InfoIcon color="blue.500" />
                                <Text fontSize="sm" color="blue.700" fontWeight="bold">Modo Visualização — Ações bloqueadas na Etapa de Processamento</Text>
                            </HStack>
                        </Box>
                    )}
                    {reportedProblems.length > 0 && (
                        <Box bg="red.100" borderColor="red.300" borderWidth="1px" p={3} borderRadius="md" mb={4}>
                            <Heading size="sm" color="red.700" mb={2}>
                                <WarningIcon mr={2} />
                                Problemas Relatados
                            </Heading>
                            <List spacing={1}>
                                {reportedProblems.map((problem, index) => (
                                    <ListItem key={index} fontSize="sm" color="red.800">
                                        <ListIcon as={WarningIcon} color="red.500" />
                                        {problem}
                                    </ListItem>
                                ))}
                            </List>
                            {productToVerify?.verificationDetails?.fotoCorrigidaAt && (
                                <Flex align="center" gap={2} mt={2} bg="green.100" borderRadius="md" px={3} py={2}>
                                    <CheckCircleIcon color="green.600" boxSize={3} />
                                    <Text fontSize="xs" color="green.800" fontWeight="semibold">
                                        Imagem já corrigida em {new Date(productToVerify.verificationDetails.fotoCorrigidaAt).toLocaleString('pt-BR')}. Agora é só dar entrada em estoque ou liberar com ressalva.
                                    </Text>
                                </Flex>
                            )}
                        </Box>
                    )}
                    {observationText && (
                        <Box bg="blue.50" borderColor="blue.200" borderWidth="1px" p={3} borderRadius="md" mb={4}>
                            <Heading size="sm" color="blue.700" mb={2}>
                                <InfoIcon mr={2} />
                                Observação Registrada
                            </Heading>
                            <Text fontSize="sm" color="gray.800" mt={1} p={2} bg="white" borderRadius="md" whiteSpace="pre-wrap">
                                {observationText}
                            </Text>
                        </Box>
                    )}

                    <Flex
                        mb={3}
                        justify="space-between"
                        align={{ base: "flex-start", md: "center" }}
                        direction={{ base: "column", md: "row" }}
                        gap={2}
                    >
                        <HStack spacing={2} align="center" flexWrap="wrap">
                            <Text fontSize="sm">Cliente: <strong>{productToVerify.customerName}</strong></Text>
                            {productToVerify.entrega_confirmada_manual && (
                                <Badge colorScheme="green" variant="solid" fontSize="0.7em" borderRadius="md">✓ ENTREGA CONFIRMADA</Badge>
                            )}
                        </HStack>

                        <HStack spacing={2} align="center">
                            {isFlex && (
                                <Box w="25px" title="Envio Flex">
                                    <Image
                                        src="/logo_flex_ml.webp"
                                        alt="Logo Flex Mercado Livre"
                                        w="100%"
                                        h="25px"
                                        objectFit="contain"
                                        borderRadius="full"
                                    />
                                </Box>
                            )}
                            {modoEnvio && (
                                <Badge colorScheme="blue" variant="solid" fontSize="0.8em">
                                    {modoEnvio}
                                </Badge>
                            )}
                        </HStack>
                    </Flex>

                    <VStack spacing={4} align="stretch">
                        <Box p={3} borderWidth="1px" borderRadius="md" bg="white">
                            <Flex direction={{ base: 'column', lg: 'row' }} justify="space-between">
                                <HStack spacing={4} align="flex-start" flex={1} mb={{ base: 4, lg: 0 }}>
                                    <VStack spacing={1} align="center">
                                        <ProdutoImagem
                                            produto={productToVerify?.produtoEstoque}
                                            origem={null}
                                            width={"100px"}
                                            height={"100px"}
                                            zoomable={true}
                                        />
                                        <Text fontSize="10px" color="gray.500" fontStyle="italic">
                                            Clique ou passe o mouse para ampliar
                                        </Text>
                                        <Box mt={2}>
                                            <Notas
                                                handleAddObs={handleAddObs}
                                                idVenda={productToVerify.vendaId}
                                                handleChangeTextObs={handleChangeTextObs}
                                                textObs={textObs}
                                                observacoes={productToVerify.observacoes ? productToVerify.observacoes : []}
                                                handleApagarObs={handleApagarObs}
                                            />
                                        </Box>
                                    </VStack>

                                    {/* ######### ÁREA DE DETALHES ATUALIZADA ######### */}
                                    <VStack align="flex-start" flex={1} spacing={1}>
                                        <Heading size="sm">{productToVerify.name}</Heading>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold">
                                            Fornecedor: <Badge variant="subtle" colorScheme="blue">{productToVerify.fornecedor}</Badge>
                                        </Text>
                                        <Text fontSize="xs" color="gray.600">SKU: {productToVerify.id}</Text>
                                        <Text fontSize="xs" color="gray.600">Código Global: {productToVerify.globalCode}</Text>
                                        <Text fontSize="xs" color="gray.600">Código Fornecedor: {productToVerify?.produtoEstoque?.codigo_fornecedor}</Text>
                                        <Text fontSize="xs" color="gray.600">Código Fabricante: {productToVerify?.produtoEstoque?.codigo_fabricante}</Text>
                                        <Text fontSize="xs" color="gray.600">Código de Barra: {productToVerify?.produtoEstoque?.codigo_barra}</Text>
                                        <Text fontSize="xs" color="gray.600">Marca: {productToVerify.brand}</Text>

                                        {qtdEmbalagem != null && (
                                            <Text fontSize="xs" color="gray.600" fontWeight="bold">
                                                Qtd. por Embalagem: <Badge colorScheme="teal" variant="outline">{qtdEmbalagem}</Badge>
                                            </Text>
                                        )}

                                        {vendeEmbalagemFechada && (
                                            <Box
                                                bg="teal.50"
                                                borderColor="teal.200"
                                                borderWidth="1px"
                                                borderRadius="md"
                                                px={2}
                                                py={1.5}
                                                w="full"
                                            >
                                                <Text fontSize="xs" color="teal.800" fontWeight="bold">
                                                    Vendido em embalagem fechada
                                                </Text>
                                                <Text fontSize="xs" color="teal.700" lineHeight="1.4">
                                                    Esperado: {productToVerify.quantity} {productToVerify.quantity > 1 ? 'embalagens' : 'embalagem'} de {qtdEmbalagem} = <strong>{totalPecasEsperadas} peças</strong>.
                                                    Confira em <strong>embalagens</strong>, não em peças.
                                                </Text>
                                            </Box>
                                        )}

                                        {mlLink && (
                                            <Link href={mlLink} isExternal color="blue.500" fontSize="sm" pt={1} _hover={{ textDecoration: 'underline' }}>
                                                Ver Anúncio <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        )}

                                        {productToVerify.estoque_taga > 0 && (
                                            <Box pt={2}>
                                                <Flex align="center" gap={2} flexWrap="wrap">
                                                    <Badge
                                                        colorScheme="red"
                                                        variant="solid"
                                                        px={2}
                                                        py={1}
                                                        borderRadius="md"
                                                    >
                                                        <VStack align="center" spacing={0}>
                                                            <Text fontSize="0.9em" fontWeight="bold" lineHeight="1.2">
                                                                ({productToVerify.estoque_taga}) ESTOQUE TAGA
                                                            </Text>
                                                            <Text fontSize="0.9em" fontStyle="italic" lineHeight="1">
                                                                ({productToVerify.localizacao_estoque})
                                                            </Text>
                                                        </VStack>
                                                    </Badge>
                                                    {!isReadOnly && (
                                                        <Button
                                                            size="xs"
                                                            colorScheme="red"
                                                            variant="outline"
                                                            leftIcon={<Icon as={FaExclamationTriangle} />}
                                                            onClick={() => setFuroEstoqueDialogOpen(true)}
                                                            isDisabled={furoEstoqueDone}
                                                            title="Marcar como Furo de Estoque TAGA: desvincula este item do estoque físico e libera para a próxima lista de compras"
                                                        >
                                                            Furo Estoque TAGA
                                                        </Button>
                                                    )}
                                                </Flex>
                                            </Box>
                                        )}
                                    </VStack>
                                    {/* ######### FIM DA ÁREA ATUALIZADA ######### */}

                                </HStack>
                                <Flex justify="end" align="end">
                                    <FormControl w="auto">
                                        <FormLabel fontSize="xs" textAlign="center" mb={2}>
                                            {vendeEmbalagemFechada ? 'Embalagens Conferidas' : 'Unidades Conferidas'}
                                        </FormLabel>
                                        <HStack spacing={0}>
                                            <Input
                                                type="text"
                                                inputMode="numeric"
                                                value={inputQuantity}
                                                onChange={handleQuantityChange}
                                                placeholder="Qtd."
                                                textAlign="center"
                                                size={{ base: 'md', md: 'md' }}
                                                w={{ base: '120px' }}
                                                borderRadius="0"
                                                bg={"gray.100"}
                                                isReadOnly={isReadOnly}
                                            />
                                            {!isReadOnly && (
                                                <>
                                                    <IconButton
                                                        aria-label="Diminuir quantidade"
                                                        icon={<MinusIcon />}
                                                        onClick={handleDecrement}
                                                        w={"75px"}
                                                        colorScheme="red"
                                                        borderRightRadius="0"
                                                    />
                                                    <IconButton
                                                        aria-label="Aumentar quantidade"
                                                        icon={<AddIcon />}
                                                        onClick={handleIncrement}
                                                        w={"75px"}
                                                        colorScheme="green"
                                                        borderLeftRadius="0"
                                                    />
                                                </>
                                            )}
                                        </HStack>
                                    </FormControl>
                                </Flex>
                            </Flex>
                            <VStack align="stretch" mt={3} pt={3} borderTopWidth={{ base: "1px", lg: "0" }} borderColor="gray.200" spacing={3}>
                                <Box>
                                    <HStack>
                                        <Icon as={WarningTwoIcon} color="red.500" />
                                        <Text fontSize="xs" fontWeight="bold">Reportar problema:</Text>
                                    </HStack>
                                    <HStack spacing={3} mt={1} wrap="wrap">
                                        <Checkbox size="md" colorScheme='red' isChecked={dataIssues.photo} isDisabled={isReadOnly} onChange={(e) => setDataIssues(p => ({ ...p, photo: e.target.checked }))}>Foto</Checkbox>
                                        <Checkbox size="md" colorScheme='red' isChecked={dataIssues.name} isDisabled={isReadOnly} onChange={(e) => setDataIssues(p => ({ ...p, name: e.target.checked }))}>Nome</Checkbox>
                                        <Checkbox size="md" colorScheme='red' isChecked={dataIssues.code} isDisabled={isReadOnly} onChange={(e) => setDataIssues(p => ({ ...p, code: e.target.checked }))}>Código</Checkbox>
                                        <Checkbox size="md" colorScheme='red' isChecked={dataIssues.brand} isDisabled={isReadOnly} onChange={(e) => setDataIssues(p => ({ ...p, brand: e.target.checked }))}>Marca</Checkbox>
                                        <Checkbox size="md" colorScheme='red' isChecked={dataIssues.avaria} isDisabled={isReadOnly} onChange={(e) => setDataIssues(p => ({ ...p, avaria: e.target.checked }))}>Avaria</Checkbox>
                                    </HStack>
                                </Box>
                                <Divider />
                                <Box>
                                    <HStack>
                                        <Icon as={InfoIcon} color="blue.500" />
                                        <Text fontSize="xs" fontWeight="bold">Outras Informações:</Text>
                                    </HStack>
                                    <Box mt={2}>
                                        <Checkbox
                                            size="md"
                                            isChecked={outroFornecedor.checked}
                                            isDisabled={isReadOnly}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                setOutroFornecedor({
                                                    checked: isChecked,
                                                    nome: isChecked ? outroFornecedor.nome : ''
                                                });
                                            }}
                                        >
                                            Produto veio de outro fornecedor (Furo de Estoque no Fornecedor principal)
                                        </Checkbox>
                                        {outroFornecedor.checked && (
                                            <Input
                                                placeholder="Digite o nome do fornecedor"
                                                value={outroFornecedor.nome}
                                                onChange={(e) => setOutroFornecedor({ ...outroFornecedor, nome: e.target.value })}
                                                size="sm"
                                                mt={2}
                                                focusBorderColor="blue.500"
                                                isReadOnly={isReadOnly}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                <FormControl>
                                    <FormLabel htmlFor={`obs-${productToVerify.id}`} fontSize="xs" fontWeight="bold">Observações do Produto</FormLabel>
                                    <Textarea id={`obs-${productToVerify.id}`} value={observation} onChange={(e) => setObservation(e.target.value)} placeholder="Ex: A imagem do anúncio está diferente do produto..." size="sm" isReadOnly={isReadOnly} />
                                </FormControl>
                            </VStack>
                        </Box>
                    </VStack>
                </ModalBody>
                <ModalFooter px={{ base: 3, md: 6 }}>
                    <Flex w="100%" gap={2} flexWrap="wrap" align="center" justify={{ base: 'stretch', md: 'flex-end' }}>
                        <Button size="sm" colorScheme="gray" onClick={onClose} mr={{ md: 'auto' }} flex={{ base: '1 1 100%', md: '0 0 auto' }}>
                            {isReadOnly ? "Fechar" : "Cancelar"}
                        </Button>

                        {canMarkAsDelivered && (
                            <Button
                                size="sm"
                                colorScheme="purple"
                                onClick={() => onMarkAsDelivered(productToVerify)}
                                flex={{ base: '1 1 auto', md: '0 0 auto' }}
                            >
                                Marcar como Entregue
                            </Button>
                        )}

                        {isReadOnly ? (
                            (!incompleteVendaIds || !incompleteVendaIds.has(productToVerify.vendaId)) && onProcessIndividual && (
                                <Button
                                    size="sm"
                                    colorScheme="blue"
                                    leftIcon={<Icon as={FaPlay} boxSize={3} />}
                                    onClick={() => {
                                        onClose();
                                        onProcessIndividual(productToVerify);
                                    }}
                                    flex={{ base: '1 1 auto', md: '0 0 auto' }}
                                >
                                    PROCESSAR
                                </Button>
                            )
                        ) : (
                            <>
                                {productToVerify.verificationDetails && (
                                    <Button
                                        size="sm"
                                        colorScheme="yellow"
                                        leftIcon={<RepeatIcon />}
                                        onClick={handleRevertClick}
                                        isLoading={isReverting}
                                        flex={{ base: '1 1 auto', md: '0 0 auto' }}
                                    >
                                        Reverter
                                    </Button>
                                )}
                                {productToVerify?.verificationDetails?.status === 2 && onDarEntradaEstoque && (
                                    <Tooltip label={imagemPendenteCorrecao ? AJUDA_ENTRADA_FOTO : AJUDA_ENTRADA_ESTOQUE} hasArrow placement="top" openDelay={200}>
                                        <Button
                                            size="sm"
                                            colorScheme="orange"
                                            variant="outline"
                                            leftIcon={<Icon as={FaWarehouse} boxSize={3} />}
                                            rightIcon={<QuestionOutlineIcon boxSize={3} />}
                                            onClick={() => {
                                                onClose();
                                                onDarEntradaEstoque(productToVerify);
                                            }}
                                            flex={{ base: '1 1 auto', md: '0 0 auto' }}
                                        >
                                            {imagemPendenteCorrecao ? 'Entrada + corrigir foto' : 'Dar entrada em estoque'}
                                        </Button>
                                    </Tooltip>
                                )}
                                {onCorrigirImagem && !productToVerify?.verificationDetails?.fotoCorrigidaAt && (
                                    (productToVerify?.verificationDetails?.status === 2 && productToVerify?.verificationDetails?.issues?.photo)
                                    || productToVerify?.verificationDetails?.status === 5
                                ) && (
                                    <Tooltip label={AJUDA_SO_CORRIGIR} hasArrow placement="top" openDelay={200}>
                                        <Button
                                            size="sm"
                                            colorScheme="purple"
                                            variant="outline"
                                            leftIcon={<Icon as={FaImage} boxSize={3} />}
                                            rightIcon={<QuestionOutlineIcon boxSize={3} />}
                                            onClick={() => {
                                                onClose();
                                                onCorrigirImagem(productToVerify);
                                            }}
                                            flex={{ base: '1 1 auto', md: '0 0 auto' }}
                                        >
                                            {productToVerify?.verificationDetails?.status === 5 ? 'Corrigir imagem (foto real)' : 'Só corrigir foto'}
                                        </Button>
                                    </Tooltip>
                                )}
                                {productToVerify?.verificationDetails?.status === 2 && (
                                    <Tooltip
                                        label={imagemPendenteCorrecao ? 'Corrija a imagem do produto antes de liberar com ressalva.' : ''}
                                        hasArrow
                                        placement="top"
                                        isDisabled={!imagemPendenteCorrecao}
                                    >
                                    <Button
                                        size="sm"
                                        colorScheme="orange"
                                        isDisabled={imagemPendenteCorrecao}
                                        onClick={() => {
                                            const enteredQuantity = parseInt(inputQuantity, 10) || 0;
                                            const verificationDetails = {
                                                checkedQuantity: enteredQuantity,
                                                issues: dataIssues,
                                                outroFornecedor: outroFornecedor,
                                                observation: observation,
                                                verifiedAt: new Date().toISOString(),
                                            };
                                            const verifiedProduct = { ...productToVerify, verificationDetails };
                                            toast({
                                                title: 'Produto liberado com ressalva!',
                                                status: 'info',
                                                duration: 3000,
                                                isClosable: true,
                                            });
                                            onConfirm(verifiedProduct, 'ressalva');
                                            onClose();
                                        }}
                                        flex={{ base: '1 1 auto', md: '0 0 auto' }}
                                    >
                                        Liberar c/ Ressalva
                                    </Button>
                                    </Tooltip>
                                )}
                                <Button size="sm" colorScheme="green" onClick={handleConfirmClick} flex={{ base: '1 1 auto', md: '0 0 auto' }}>Confirmar</Button>
                            </>
                        )}
                    </Flex>
                </ModalFooter>
            </ModalContent>

            <AlertDialog
                isOpen={furoEstoqueDialogOpen}
                leastDestructiveRef={cancelFuroRef}
                onClose={() => !furoEstoqueLoading && setFuroEstoqueDialogOpen(false)}
                size={alertDialogSize}
                isCentered
                closeOnOverlayClick={!furoEstoqueLoading}
                closeOnEsc={!furoEstoqueLoading}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.600">
                            <HStack>
                                <Icon as={FaExclamationTriangle} />
                                <Text>Confirmar Furo de Estoque TAGA</Text>
                            </HStack>
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <VStack align="stretch" spacing={3}>
                                <Box bg="red.50" border="1px solid" borderColor="red.200" p={3} borderRadius="md">
                                    <Text fontSize="sm" fontWeight="bold" color="red.700" mb={1}>
                                        Produto não localizado fisicamente?
                                    </Text>
                                    <Text fontSize="xs" color="red.700">
                                        Use esta ação apenas quando confirmar que o item realmente <strong>não está no estoque físico</strong>.
                                    </Text>
                                </Box>

                                {productToVerify && (
                                    <Box bg="gray.50" p={3} borderRadius="md" fontSize="sm">
                                        <Text><strong>Venda:</strong> {productToVerify.orderId}</Text>
                                        <Text><strong>Produto:</strong> {productToVerify.name}</Text>
                                        <Text><strong>SKU:</strong> {productToVerify.id}</Text>
                                        <Text>
                                            <strong>Qtd vinculada:</strong> {productToVerify.estoque_taga} unid.{' '}
                                            <strong>Local:</strong> {productToVerify.localizacao_estoque || '-'}
                                        </Text>
                                    </Box>
                                )}

                                <Box fontSize="xs" color="gray.700">
                                    <Text fontWeight="bold" mb={1}>Ao confirmar:</Text>
                                    <VStack align="start" spacing={1} pl={2}>
                                        <Text>• A etiqueta ESTOQUE TAGA será removida deste item</Text>
                                        <Text>• A baixa do estoque <strong>NÃO será revertida</strong> mesmo se a venda for cancelada</Text>
                                        <Text>• O produto entra automaticamente na próxima lista de compras</Text>
                                        <Text>• Notificação será enviada à equipe</Text>
                                    </VStack>
                                </Box>

                                <Box bg="yellow.50" border="1px solid" borderColor="yellow.200" p={2} borderRadius="md">
                                    <Text fontSize="xs" color="yellow.800">
                                        <strong>Atenção:</strong> esta ação é definitiva e não pode ser desfeita pela tela.
                                    </Text>
                                </Box>
                            </VStack>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button
                                ref={cancelFuroRef}
                                onClick={() => setFuroEstoqueDialogOpen(false)}
                                isDisabled={furoEstoqueLoading}
                                size="sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleConfirmFuroEstoque}
                                ml={3}
                                isLoading={furoEstoqueLoading}
                                loadingText="Registrando..."
                                size="sm"
                                leftIcon={<Icon as={FaExclamationTriangle} />}
                            >
                                Confirmar Furo
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Modal>
    );
};